import { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import omit from 'lodash/omit';

export type CustomOnChangeEvent =
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  | CustomEvent;

export type HandleInputChange = (e: CustomOnChangeEvent) => void;

export type FormFieldProps<T> = {
  error?: boolean;
  helperText?: string;
  id: keyof T;
  name: keyof T;
  onChange: HandleInputChange;
  value: T[keyof T];
};

export type GetInputProps<T> = (name: keyof T) => FormFieldProps<T>;

export interface UseFormParameters<T> {
  initialState: T;
  onSubmit: (x: T) => void;
}

export function useForm<T>({ initialState, onSubmit }: UseFormParameters<T>) {
  const [formData, setFormData] = useState<T>(initialState);
  const [submittedOnce, setSubmittedOnce] = useState(false);

  const handleInputChange = (e: CustomOnChangeEvent) => {
    let name: string | undefined;
    let value: any;

    if (e instanceof CustomEvent) {
      name = e.detail?.name;
      value = e.detail?.value;
    } else if (e.currentTarget) {
      name = e.currentTarget.name;
      value = e.currentTarget.value;
    }

    if (name) {
      setFormData({ ...formData, [name]: value });
    }
  };

  function getInputProps(name: keyof T) {
    return {
      id: name,
      name,
      onChange: handleInputChange,
      value: formData[name],
    };
  }

  const handleSubmit = (e: SyntheticEvent) => {
    setSubmittedOnce(true);
    e.preventDefault();
    onSubmit?.(formData);
  };

  return {
    formData,
    getInputProps,
    handleInputChange,
    handleSubmit,
    submittedOnce,
  };
}

export type Validations<T> = {
  [P in keyof T]?: (value: T[keyof T], formData: T) => string | false;
};

export type UseValidatedFormParameters<T> = UseFormParameters<T> & {
  validations: Validations<T>;
};

export type ErrorsMap<T> = {
  [P in keyof T]?: string | undefined;
};

export type FormCtrl<T> = {
  errors: ErrorsMap<T> | null;
  formData: T;
  getInputProps: GetInputProps<Partial<T>>;
  handleInputChange: HandleInputChange;
  handleSubmit: (e: SyntheticEvent) => void;
  isValid: boolean;
  setErrorOverrides: any;
  submittedOnce: boolean;
};

export function useValidatedForm<T>({
  initialState,
  onSubmit,
  validations = {},
}: UseValidatedFormParameters<T>): FormCtrl<T> {
  const [errors, setErrors] = useState<ErrorsMap<T>>({});
  const [errorOverrides, setErrorOverrides] = useState<ErrorsMap<T>>({});
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const { formData, getInputProps, handleInputChange, handleSubmit } = useForm({
    initialState,
    onSubmit,
  });
  const isValid = !Object.values(errors).filter((v) => v).length;

  function clearOverride(field: keyof T) {
    setErrorOverrides(omit<ErrorsMap<T>>(errorOverrides, field));
  }

  useEffect(() => {
    setErrors(
      (
        Object.entries(formData as any) as unknown as [keyof T, T[keyof T]][]
      ).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: validations[key]?.(value, formData),
        }),
        {}
      ) as ErrorsMap<T>
    );
  }, [formData, validations]);

  const renderErrors = { ...errors, ...errorOverrides };

  function _getInputProps(name: keyof T) {
    const inputProps = getInputProps(name);
    return submittedOnce
      ? {
          ...inputProps,
          error: !!renderErrors[name],
          ...(renderErrors[name] && { helperText: renderErrors[name] }),
          onChange: (e: CustomOnChangeEvent) => {
            clearOverride(name);
            inputProps.onChange(e);
          },
        }
      : inputProps;
  }

  const _handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setSubmittedOnce(true);
    isValid && handleSubmit(e);
  };

  return {
    errors,
    formData,
    getInputProps: _getInputProps,
    handleInputChange,
    handleSubmit: _handleSubmit,
    isValid,
    setErrorOverrides,
    submittedOnce,
  };
}
